import React, { useState } from 'react';
import { View, ViewStyle, StyleProp, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask } from 'react-native-masked-text';
import { translate } from '../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

export type PhoneCode = string;

type MyPhoneInputProps = {
    label: string;
    placeholder?: string;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    required?: boolean;
    disabled?: boolean;
} & ({
    value: string;
    invalid: (value: string) => boolean;
    onChangeText: (value: string) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    value?: never;
    invalid?: never;
    onChangeText?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
})

export default function MyPhoneInput({ label, placeholder, value, invalid, onChangeText, viewStyle, labelStyle, required, form, fieldName, customValidations, pattern, disabled }: MyPhoneInputProps) {
    const [isInvalid, setIsInvalid] = useState(false);

    const { styles, theme } = useStyles(stylesheet);

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};
        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[{ rowGap: 5 }, viewStyle]}>
                    <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                        <MyAppText style={[styles.label, labelStyle]}>{label}</MyAppText>
                        {required ?
                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInputMask
                        placeholder={placeholder}
                        onBlur={field.onBlur}
                        value={field.value}
                        includeRawValueInChangeText={true}
                        onChangeText={(maskedText, rawText) => {
                            field.onChange(rawText || '');
                        }}
                        type={'cel-phone'}
                        style={[styles.input, {
                            color: field.value == '' ? theme.colors.placeholder : theme.colors.textColor,
                        }, disabled && styles.disabled,
                        isInvalid ? styles.invalid : undefined,]}
                        options={{
                            maskType: 'BRL',
                            withDDD: true,
                            dddMask: '(99) '
                        }}
                        editable={!disabled}
                    />
                    {isInvalid ?
                        <MyAppText style={styles.error}>{translate('invalidPhone')}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;
    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[{ rowGap: 5 }, viewStyle]}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={[styles.label, labelStyle]}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    placeholder={placeholder}
                    value={value}
                    includeRawValueInChangeText={true}
                    onChangeText={(maskedText, rawText) => {
                        onChangeText(rawText || '');
                    }}
                    type={'cel-phone'}
                    style={[styles.input, {
                        color: value == '' ? theme.colors.placeholder : theme.colors.textColor,
                    }, disabled && styles.disabled,
                    isInvalid ? styles.invalid : undefined]}
                    options={{
                        maskType: 'BRL',
                        withDDD: true,
                        dddMask: '(99) '
                    }}
                    onBlur={() => {
                        setIsInvalid(invalid(value));
                    }}
                    editable={!disabled}
                />
                {isInvalid ?
                    <MyAppText style={styles.error}>{translate('invalidPhone')}</MyAppText> : null}
            </View>
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    label: {
        color: theme.colors.labelColor,
        height: 25,
    },
    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        maxHeight: 40,
        padding: 10,
        borderRadius: 4,
        color: theme.colors.field.text,
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
    invalid: {
        borderColor: '#FF0000',
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
}));
